import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight, Sheet, X } from 'lucide-react';
import dayjs from 'dayjs';
import { cn } from '@/lib/utils';
import { ColumnKind, extractSheetId, loadGoogleSheet, SheetLoadError, SheetTab, sheetUrl } from './loadGoogleSheet';

const LAST_SHEET_KEY = 'sheetViewer:lastSheetId';

const readLastSheetLink = () => {
  try {
    const id = localStorage.getItem(LAST_SHEET_KEY);
    return id ? sheetUrl(id) : '';
  } catch {
    return '';
  }
};

const REFRESH_INTERVAL_MS = 3000;

const COLUMN_CLASSES: Record<ColumnKind, string> = {
  index: 'w-px whitespace-nowrap text-right tabular-nums text-gray-400 dark:text-gray-500',
  text: 'text-left',
  question: 'w-px whitespace-nowrap text-center tabular-nums',
  total: 'w-px whitespace-nowrap text-right tabular-nums font-semibold',
};

// The first two columns (usually # and team name) stay pinned while the
// question columns scroll horizontally.
const STICKY_COLUMNS = 2;
// Opaque so scrolled cells don't show through; the stripe colour is gray-800/40 over gray-900 pre-mixed.
// Divider on the pinned area's right edge.
const STICKY_EDGE = 'shadow-[inset_-1px_0_0_rgb(229,231,235)] dark:shadow-[inset_-1px_0_0_rgb(55,65,81)]';
const ROW_BG = 'bg-white dark:bg-gray-900 even:bg-gray-50 dark:even:bg-[#171f2d]';

const SheetViewer: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sheetId = searchParams.get('id');

  const [input, setInput] = useState(() => (sheetId ? sheetUrl(sheetId) : readLastSheetLink()));
  const [inputError, setInputError] = useState('');
  const [title, setTitle] = useState('');
  const [tabs, setTabs] = useState<SheetTab[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  // Left offset of the second sticky column = rendered width of the first one.
  const [firstColWidth, setFirstColWidth] = useState(0);
  const firstHeaderCell = useRef<HTMLTableCellElement | null>(null);
  const stickyStyle = (c: number): React.CSSProperties | undefined =>
    c < STICKY_COLUMNS ? { left: c === 0 ? 0 : firstColWidth } : undefined;

  // Guards against overlapping requests and responses for a sheet that is no longer open.
  const inFlight = useRef<string | null>(null);
  const currentId = useRef(sheetId);
  currentId.current = sheetId;

  const load = useCallback(async (id: string, silent = false) => {
    if (inFlight.current === id) return;
    inFlight.current = id;
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const loaded = await loadGoogleSheet(id);
      if (currentId.current !== id) return;
      setTitle(loaded.title);
      setTabs(loaded.tabs);
      setLastUpdated(new Date());
      setActiveTab((current) => (current < loaded.tabs.length ? current : 0));
      setError('');
      try {
        localStorage.setItem(LAST_SHEET_KEY, id);
      } catch {
        // Remembering the last sheet is only a convenience.
      }
    } catch (e) {
      if (currentId.current !== id) return;
      // A failed background refresh keeps the last good data on screen.
      if (silent) return;
      setTabs([]);
      setError(e instanceof SheetLoadError ? e.message : 'Cədvəl oxunarkən xəta baş verdi.');
    } finally {
      if (inFlight.current === id) inFlight.current = null;
      if (!silent) setLoading(false);
    }
  }, []);

  // Re-attach whenever the table re-renders with a different first header cell.
  useEffect(() => {
    const cell = firstHeaderCell.current;
    if (!cell) return;
    const measure = () => setFirstColWidth(cell.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(cell);
    return () => observer.disconnect();
  }, [tabs, activeTab]);

  useEffect(() => {
    setActiveTab(0);
    setTitle('');
    setTabs([]);
    setLastUpdated(null);
    if (sheetId) load(sheetId);
  }, [sheetId, load]);

  useEffect(() => {
    if (!sheetId) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load(sheetId, true);
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [sheetId, load]);

  useEffect(() => {
    if (!title) return;
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const id = extractSheetId(input);
    if (!id) {
      setInputError('Düzgün Google Sheets linki daxil edin.');
      return;
    }
    setInputError('');
    if (id === sheetId) load(id);
    else setSearchParams({ id });
  };

  const handleClose = () => {
    setSearchParams({});
    setError('');
  };

  if (!sheetId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-24 pb-12">
        <div className="mx-auto max-w-xl px-4 sm:px-6 lg:px-8 mt-12">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center p-3 bg-amber-100/50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-2xl mb-4">
              <Sheet className="w-8 h-8" />
            </div>
            <h1 className="text-4xl font-black text-gray-900 dark:text-gray-50 tracking-tight">Cədvəli izlə</h1>
            <p className="mt-3 text-lg text-gray-500 dark:text-gray-400">
              Google Sheets cədvəlini açın — hər vərəq ayrıca tab kimi göstəriləcək.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-100 dark:border-gray-800">
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div>
                <label htmlFor="sheetLink" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                  Google Sheets linki
                </label>
                <input
                  id="sheetLink"
                  type="url"
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    setInputError('');
                  }}
                  placeholder="https://docs.google.com/spreadsheets/d/…"
                  autoFocus
                  className={cn(
                    'block w-full rounded-xl bg-gray-50 dark:bg-gray-950 px-4 py-3 text-gray-900 dark:text-gray-50 font-mono text-sm focus:bg-white dark:focus:bg-gray-900 focus:ring-2 transition-colors',
                    inputError
                      ? 'border-red-400 dark:border-red-500 focus:border-red-500 focus:ring-red-200'
                      : 'border-gray-200 dark:border-gray-700 focus:border-amber-500 focus:ring-amber-200'
                  )}
                  required
                  aria-invalid={!!inputError}
                  aria-describedby={inputError ? 'sheetLinkError' : undefined}
                />
                {inputError && <p id="sheetLinkError" role="alert" className="mt-1.5 text-sm text-red-600 dark:text-red-400">{inputError}</p>}
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Cədvəl <b>“Linki olan hər kəs baxa bilər”</b> kimi paylaşılmalıdır. Linki Google Sheets-də
                  <b className="mx-1">Paylaş → Linki kopyala</b>ilə əldə edə bilərsiniz.
                </p>
              </div>
              <button
                type="submit"
                disabled={!input.trim()}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-3.5 text-sm font-bold text-white transition-all hover:bg-amber-600 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Aç
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const tab = tabs[activeTab];
  const captions = tab?.captions.filter((caption) => caption !== title) ?? [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-24 pb-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="rounded-xl bg-amber-100 dark:bg-amber-900/40 p-2.5 text-amber-600 dark:text-amber-400">
              <Sheet className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-black text-gray-900 dark:text-gray-50 truncate">
                {title || (loading ? '…' : 'Cədvəl')}
              </h1>
              {lastUpdated && (
                <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Son yenilənmə: {dayjs(lastUpdated).format('HH:mm:ss')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <X className="h-4 w-4" />
              Bağla
            </button>
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-6 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        ) : loading && tabs.length === 0 ? (
          <div className="animate-pulse rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-4 rounded-full bg-gray-100 dark:bg-gray-800" />
            ))}
          </div>
        ) : tab ? (
          <div className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
            {tabs.length > 1 && (
              <div role="tablist" className="flex gap-1 overflow-x-auto border-b border-gray-100 dark:border-gray-800 px-2 pt-2">
                {tabs.map((t, i) => (
                  <button
                    key={t.name}
                    role="tab"
                    aria-selected={i === activeTab}
                    onClick={() => setActiveTab(i)}
                    className={cn(
                      'whitespace-nowrap rounded-t-lg px-4 py-2 text-sm font-semibold transition-colors border-b-2 -mb-px',
                      i === activeTab
                        ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                    )}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            )}

            {captions.length > 0 && (
              <div className="border-b border-gray-100 dark:border-gray-800 px-4 py-3 text-sm font-bold text-gray-900 dark:text-gray-50">
                {captions.join(' · ')}
              </div>
            )}

            {tab.header.length === 0 ? (
              <p className="p-6 text-sm text-gray-500 dark:text-gray-400">Bu vərəq boşdur.</p>
            ) : (
              <div className="overflow-auto max-h-[70vh]">
                {/* border-separate: collapsed borders don't travel with sticky cells */}
                <table className="w-full text-sm border-separate border-spacing-0">
                  <thead>
                    <tr>
                      {tab.header.map((cell, c) => (
                        <th
                          key={c}
                          ref={c === 0 ? firstHeaderCell : undefined}
                          style={stickyStyle(c)}
                          className={cn(
                            'sticky top-0 z-10 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-3 py-2 font-bold text-gray-900 dark:text-gray-50',
                            COLUMN_CLASSES[tab.columns[c]],
                            c < STICKY_COLUMNS && 'z-20',
                            c === STICKY_COLUMNS - 1 && STICKY_EDGE
                          )}
                        >
                          {cell}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tab.body.map((row, r) => (
                      <tr key={r} className={ROW_BG}>
                        {tab.header.map((_, c) => (
                          <td
                            key={c}
                            style={stickyStyle(c)}
                            className={cn(
                              'border-b border-gray-100 dark:border-gray-800 px-3 py-2 text-gray-700 dark:text-gray-200 align-top',
                              COLUMN_CLASSES[tab.columns[c]],
                              c < STICKY_COLUMNS && 'sticky z-[5] bg-inherit',
                              c === STICKY_COLUMNS - 1 && STICKY_EDGE
                            )}
                          >
                            {row[c]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SheetViewer;
